import {
  FeatureImpactScore_Value,
  Severity,
  severityToJSON,
  ThemeType,
  themeTypeToJSON,
  type Insight,
  type InsightLinkedSignal,
} from "@devplaninc/codeup";
import type {InsightWithEvidence} from "@/hooks/queries/use-insights";
import {formatEnumLabel, summarize, toTitleCase} from "@/lib/utils";

export type InsightKind = "feedbackTheme" | "risk" | "featureRequest" | "unknown";

const KIND_LABELS: Record<InsightKind, string> = {
  feedbackTheme: "Feedback theme",
  risk: "Risk",
  featureRequest: "Feature request",
  unknown: "Insight",
};

const KIND_ORDER: InsightKind[] = ["feedbackTheme", "risk", "featureRequest", "unknown"];

export type InsightKindCount = {
  kind: InsightKind;
  kindLabel: string;
  count: number;
};

export type InsightRiskSeverityDisplay = {
  label: string;
  shortLabel: string;
  description: string;
  ariaLabel: string;
  accentClassName: string;
  cardClassName: string;
  className: string;
  iconClassName: string;
};

export function insightKind(insight: Insight): InsightKind {
  const details = insight.typeDetails;
  if (!details) return "unknown";
  switch (details.$case) {
    case "feedbackTheme":
      return "feedbackTheme";
    default:
      return details.$case;
  }
}

export function insightKindLabel(kind: InsightKind): string {
  return KIND_LABELS[kind];
}

export function insightSubtypeLabel(insight: Insight): string | undefined {
  const details = insight.typeDetails;
  if (!details) return undefined;
  switch (details.$case) {
    case "feedbackTheme":
      return formatThemeType(details.value.themeType);
    case "risk":
      return formatSeverity(details.value.severity);
    case "featureRequest":
      return formatFeatureRequestImpact(details.value.businessImpact?.score);
    default:
      return undefined;
  }
}

export function insightRiskSeverityDisplay(insight: Insight): InsightRiskSeverityDisplay | undefined {
  const details = insight.typeDetails;
  if (details?.$case !== "risk") return undefined;

  const label = formatSeverity(details.value.severity);
  if (!label) return undefined;

  const shortLabel = riskSeverityShortLabel(details.value.severity);
  const description = details.value.severityExplanation.trim() || `${label} risk severity.`;
  return {
    label,
    shortLabel,
    description,
    ariaLabel: `${label} risk severity`,
    ...riskSeverityTone(details.value.severity),
  };
}

export function insightTitle(insight: Insight): string {
  return insight.name.trim() || summarize(insight.description) || "Untitled insight";
}

export function insightTimestamp(insight: Insight): Date | undefined {
  return insight.createdAt ?? insight.updatedAt ?? undefined;
}

export function sortInsights(entries: InsightWithEvidence[]): InsightWithEvidence[] {
  return [...entries].sort(
    (a, b) => (insightTimestamp(b.insight)?.getTime() ?? 0) - (insightTimestamp(a.insight)?.getTime() ?? 0),
  );
}

export function computeKindCounts(entries: InsightWithEvidence[]): InsightKindCount[] {
  const counts = new Map<InsightKind, InsightKindCount>();
  for (const {insight} of entries) {
    const kind = insightKind(insight);
    const existing = counts.get(kind);
    if (existing) {
      existing.count += 1;
    } else {
      counts.set(kind, {kind, kindLabel: KIND_LABELS[kind], count: 1});
    }
  }
  return [...counts.values()].sort(sortByKindOrder);
}

export function filterInsights(
  entries: InsightWithEvidence[],
  search: string,
  selectedKinds: InsightKind[],
): InsightWithEvidence[] {
  const query = search.trim().toLowerCase();
  const selected = new Set<InsightKind>(selectedKinds);
  return entries.filter(({insight, linkedSignals}) => {
    const kind = insightKind(insight);
    if (selected.size > 0 && !selected.has(kind)) return false;
    if (!query) return true;
    return insightSearchText(insight, kind, linkedSignals).includes(query);
  });
}

function insightSearchText(insight: Insight, kind: InsightKind, linkedSignals: InsightLinkedSignal[]): string {
  const signalText = linkedSignals.flatMap(s => [s.signalName, s.rationale]).filter(Boolean).join(" ");
  return [
    insightTitle(insight),
    insight.description,
    KIND_LABELS[kind],
    insightSubtypeLabel(insight),
    signalText,
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();
}

function formatThemeType(theme: ThemeType): string | undefined {
  if (theme === ThemeType.THEME_TYPE_UNSPECIFIED) return undefined;
  switch (theme) {
    case ThemeType.THEME_TYPE_PROBLEM_OPPORTUNITY:
      return "Problem / opportunity";
    case ThemeType.THEME_TYPE_REQUESTED_FEATURE:
      return "Requested feature";
    default:
      return toTitleCase(themeTypeToJSON(theme).replace(/^THEME_TYPE_/, ""));
  }
}

function formatSeverity(severity: Severity): string | undefined {
  if (severity === Severity.SEVERITY_UNSPECIFIED) return undefined;
  return formatEnumLabel(severityToJSON(severity), "SEVERITY_");
}

function formatFeatureRequestImpact(score: FeatureImpactScore_Value | undefined): string | undefined {
  switch (score) {
    case FeatureImpactScore_Value.VALUE_LOW:
      return "Low impact";
    case FeatureImpactScore_Value.VALUE_MEDIUM:
      return "Medium impact";
    case FeatureImpactScore_Value.VALUE_HIGH:
      return "High impact";
    default:
      return undefined;
  }
}

function riskSeverityShortLabel(severity: Severity): string {
  switch (severity) {
    case Severity.SEVERITY_LOW:
      return "LOW";
    case Severity.SEVERITY_MEDIUM:
      return "MED";
    case Severity.SEVERITY_HIGH:
      return "HIGH";
    default:
      return "RISK";
  }
}

function riskSeverityTone(severity: Severity): Pick<
  InsightRiskSeverityDisplay,
  "accentClassName" | "cardClassName" | "className" | "iconClassName"
> {
  switch (severity) {
    case Severity.SEVERITY_LOW:
      return {
        accentClassName: "bg-slate-400",
        cardClassName: "border-border",
        className: "border-slate-400/30 bg-slate-400/10 text-slate-600 dark:text-slate-300",
        iconClassName: "text-slate-500 dark:text-slate-300",
      };
    case Severity.SEVERITY_MEDIUM:
      return {
        accentClassName: "bg-amber-500",
        cardClassName: "border-border",
        className: "border-amber-500/30 bg-amber-500/10 text-amber-700 dark:text-amber-300",
        iconClassName: "text-amber-600 dark:text-amber-300",
      };
    case Severity.SEVERITY_HIGH:
      return {
        accentClassName: "bg-red-500",
        cardClassName: "border-border",
        className: "border-red-500/30 bg-red-500/10 text-red-600 dark:text-red-300",
        iconClassName: "text-red-600 dark:text-red-300",
      };
    default:
      return {
        accentClassName: "bg-muted-foreground",
        cardClassName: "border-border",
        className: "border-border bg-muted text-muted-foreground",
        iconClassName: "text-muted-foreground",
      };
  }
}

function sortByKindOrder(a: InsightKindCount, b: InsightKindCount): number {
  const orderA = KIND_ORDER.indexOf(a.kind);
  const orderB = KIND_ORDER.indexOf(b.kind);
  if (orderA !== -1 || orderB !== -1) {
    if (orderA === -1) return 1;
    if (orderB === -1) return -1;
    return orderA - orderB;
  }
  return a.kindLabel.localeCompare(b.kindLabel);
}
